import { ReactNode, useState } from "react";
import { IonContent, IonPage, IonSplitPane, useIonRouter, UseIonRouterResult } from "@ionic/react";
import { DesktopSideMenu } from "components/DesktopSideMenu/DesktopSideMenu";
import { useAuth } from "pages";
import { MdChevronLeft } from "react-icons/md";
import {
  createRequestsPath,
  createSchoolIndexPath,
  isActiveRequestsRoute,
  isPastRequestsRoute,
  PATHS,
  SEGMENTS,
} from "routes/routeData";
import { AliasBanner, BannerContainer } from "swing-components";

import { translate } from "~utils";

import styles from "./DesktopViewWrapper.module.css";

interface DesktopViewWrapperProps {
  title?: string | (() => JSX.Element);
  children: ReactNode;
}

export default function DesktopViewWrapper(props: DesktopViewWrapperProps) {
  const { children, title } = props;
  const router = useIonRouter();
  const { isBackNavigationDisplayed, navigateBackTo, navigationBackLabel } =
    generateBackNav(router);

  const [expanded, setExpanded] = useState<boolean>(() => {
    const initialValue = window.localStorage.getItem("isMenuExpanded");
    return initialValue !== null ? JSON.parse(initialValue) : true;
  });

  const flipExpandedState = () => {
    setExpanded((old) => {
      window.localStorage.setItem("isMenuExpanded", JSON.stringify(!old));
      return !old;
    });
  };

  const { userInfo } = useAuth();

  return (
    <IonSplitPane
      when="xs"
      contentId="main-app"
      className={`${expanded ? styles.ionSplitPaneExpanded : styles.ionSplitPane}`}
    >
      {/* Left */}
      <DesktopSideMenu expanded={expanded} flipExpandedState={flipExpandedState} />

      {/* Right */}
      <IonPage id="main-app">
        <IonContent fullscreen>
          {userInfo?.isAlias && (
            <BannerContainer>
              <AliasBanner email={userInfo?.email} isAdmin />
            </BannerContainer>
          )}
          {/* Header */}
          <header
            css={{
              marginTop: isBackNavigationDisplayed ? 56 : 20,
              marginBottom: isBackNavigationDisplayed ? 40 : 10,
            }}
          >
            {/* Back Button */}
            {isBackNavigationDisplayed && (
              <div onClick={navigateBackTo} className={styles.desktopHeaderNav}>
                <MdChevronLeft className={styles.chevronLeft} />
                <span css={{ fontSize: 17 }}>{navigationBackLabel}</span>
              </div>
            )}
            {/* Page Title */}
            {title && (
              <div className={`${styles.desktopHeaderTitle} mw-800`}>
                {typeof title === "string" ? (
                  <h1 css={{ padding: "16px 0" }}>{title}</h1>
                ) : (
                  <>{title()}</>
                )}
              </div>
            )}
          </header>
          {/* Content */}
          {children}
        </IonContent>
      </IonPage>
    </IonSplitPane>
  );
}

export function generateBackNav(router: UseIonRouterResult) {
  // default values provided to avoid Storybook test issues
  const { routeInfo = { lastPathname: "", pushedByRoute: "", pathname: "" } } = router;
  const pathSegments = routeInfo.pathname.substring(1).split("/");
  const lastPathSegments = routeInfo.lastPathname?.substring(1).split("/");

  // show navigation on non-root routes
  // note: pathSegments first entry is the root route path
  // note: when arriving on sub profile from activity log we are
  // using a link via. dangerouslySetInnerHTML which means there is
  // only 1 path segment so handling this case separately
  const isNavDisplayed = pathSegments.length > 1 || window.location.hash.includes(SEGMENTS.subs);
  let navTo;
  let navLabel = translate("LABEL_BACK");

  // when creating a new request return to request index instead of create request
  if (routeInfo.lastPathname?.includes(SEGMENTS.requestASub)) {
    navLabel = translate("LABEL_ACTIVE_REQUESTS");
    navTo = () => router.push(createRequestsPath("active-requests"));
  }
  // when arrived directly to this route and associated to a past request
  else if (isPastRequestsRoute(routeInfo.pathname)) {
    navLabel = translate("LABEL_PAST_REQUESTS");
    navTo = () => router.push(createRequestsPath("past-requests"));
  }
  //  when arrived directly to this route and associated to an active request
  else if (isActiveRequestsRoute(routeInfo.pathname)) {
    navLabel = translate("LABEL_ACTIVE_REQUESTS");
    navTo = () => router.push(createRequestsPath("active-requests"));
  }

  // Schools Logic
  if (routeInfo.pathname?.includes("schools")) {
    // If arriving from active/past requests school onclick.
    if (routeInfo.lastPathname?.includes(SEGMENTS.activeRequests)) {
      // TODO: HANDLE AS PART OF ENTRY POINT TICKET
    } else if (isPastRequestsRoute(routeInfo.pathname)) {
      // TODO: HANDLE AS PART OF ENTRY POINT TICKET
    } else {
      // Entering via manually changing the URL or default.
      navLabel = translate("ROUTE_TABLE_SCHOOLS");
      navTo = () => router.push(createSchoolIndexPath());
    }
  }

  // when arrived from Activity Log
  // the pathname from the Activity Log is the 'active-requests' or 'past-requests'
  // so we need to sue the lastPathname field since that holds the path for the Request Details page
  if (
    (routeInfo.lastPathname?.includes(`${SEGMENTS.activeRequests}/`) ||
      routeInfo.lastPathname?.includes(`${SEGMENTS.pastRequests}/`)) &&
    window.location.hash.includes(SEGMENTS.subs) &&
    isNavDisplayed
  ) {
    navTo = () => router.push(routeInfo.lastPathname ? routeInfo.lastPathname : "");
  }

  // if on sub page and previous route was not the root route then set label to singular
  if (
    window.location.hash.includes(SEGMENTS.subs) &&
    lastPathSegments &&
    lastPathSegments.length > 1
  ) {
    navLabel = translate("LABEL_REQUEST_DETAILS_NAV_TITLE");
  }

  // Billing Page
  if (routeInfo.pathname === PATHS.billing) {
    navLabel = translate("BILLING_PAGE_BACK_BUTTON");
    navTo = () => router.push(PATHS.more);
  }

  // submap vs subMap. added this to support no breaking changes.
  if (routeInfo.lastPathname?.toLowerCase().includes(`${PATHS.subMap.toLowerCase()}`)) {
    navTo = () => router.goBack();
  }

  return {
    isBackNavigationDisplayed: !!isNavDisplayed,
    navigateBackTo: navTo,
    navigationBackLabel: navLabel,
  };
}
